import React, { useCallback, useMemo, useState } from 'react';
import {
  QBox,
  QButton,
  QButtonGroup,
  QDeleteConfirmationModal,
  QMenuButton,
  QMenuItemType,
  QText,
  QTooltip,
} from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { TEMPLATE_STATUS, TemplateDetail } from '../../types';
import { useSearchParams } from 'react-router-dom';
import { useCreateTemplateDraft } from '../../hooks/useCreateTemplateDraft';
import { MakeEffectiveModal } from '../MakeEffectiveModal/MakeEffectiveModal';
import { useDeleteTemplateDraft } from '../../hooks/useDeleteTemplateDraft';
import { useUpdateTemplateStatus } from '../../hooks/useUpdateTemplateStatus';
import { ChangeTemplateOwner } from '../ChangeTemplateOwner/ChangeTemplateOwner';
import { useTemplateActions } from '../../hooks/useTemplateActions';

type TemplateHeaderButtonsProps = {
  template: TemplateDetail;
  onSaveSections?: () => void;
  onBeforeMakeEffective?: () => Promise<void>;
};

export const TemplateHeaderButtons: React.FC<TemplateHeaderButtonsProps> = ({
  template,
  onSaveSections,
  onBeforeMakeEffective,
}) => {
  switch (template.status) {
    case TEMPLATE_STATUS.effective:
      return <EffectiveTemplateButtons template={template} />;
    case TEMPLATE_STATUS.archived:
      return <ArchivedTemplateButtons template={template} />;
    case TEMPLATE_STATUS.draft:
      return (
        <DraftTemplateButtons
          template={template}
          onSaveSections={onSaveSections}
          onBeforeMakeEffective={onBeforeMakeEffective}
        />
      );
    default:
      return null;
  }
};

type StatusButtonsProps = {
  template: TemplateDetail;
};

const EffectiveTemplateButtons: React.FC<StatusButtonsProps> = ({ template }) => {
  const { t } = useTranslation();
  const { canCreateDraft, canArchive } = useTemplateActions(template);
  const { mutate: createTemplateDraft, isLoading: isCreateTemplateDraftLoading } = useCreateTemplateDraft();
  const { mutate: updateTemplateStatus, isLoading: isUpdateTemplateStatusLoading } = useUpdateTemplateStatus(
    template.template_uuid,
  );

  const handleCreateNewDraftClick = useCallback(() => {
    createTemplateDraft(template.template_uuid);
  }, [createTemplateDraft, template]);

  const handleArchiveClick = useCallback(() => {
    updateTemplateStatus(TEMPLATE_STATUS.archived);
  }, [updateTemplateStatus]);

  return (
    <>
      {canCreateDraft && (
        <QButton
          onClick={handleCreateNewDraftClick}
          variant="outline"
          data-cy="createTemplateDraftButton"
          isDisabled={isCreateTemplateDraftLoading}
          isLoading={isCreateTemplateDraftLoading}
        >
          {t('template.details.buttons.createDraft')}
        </QButton>
      )}
      {canArchive && (
        <QBox data-cy="dropdownMenuMoreOption">
          <QMenuButton
            buttonLabel="options"
            itemSize="sm"
            items={[{ id: '0', label: t('template.details.buttons.archive') }]}
            onItemClick={handleArchiveClick}
            isDisabled={isUpdateTemplateStatusLoading}
            variant="icon"
          />
        </QBox>
      )}
    </>
  );
};

const ArchivedTemplateButtons: React.FC<StatusButtonsProps> = ({ template }) => {
  const { t } = useTranslation();
  const { canRestore } = useTemplateActions(template);
  const { mutate: updateTemplateStatus, isLoading: isUpdateTemplateStatusLoading } = useUpdateTemplateStatus(
    template.template_uuid,
  );

  const handleRestoreClick = useCallback(() => {
    updateTemplateStatus(TEMPLATE_STATUS.effective);
  }, [updateTemplateStatus]);

  if (!canRestore) {
    return null;
  }

  return (
    <QBox data-cy="dropdownMenuMoreOption">
      <QMenuButton
        buttonLabel="options"
        itemSize="sm"
        items={[{ id: '0', label: t('template.details.buttons.restore') }]}
        onItemClick={handleRestoreClick}
        isDisabled={isUpdateTemplateStatusLoading}
        variant="icon"
      />
    </QBox>
  );
};

const DraftTemplateButtons: React.FC<TemplateHeaderButtonsProps> = ({
  template,
  onSaveSections,
  onBeforeMakeEffective,
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canEdit, canDelete, canChangeOwner, canMakeEffective } = useTemplateActions(template);
  const isEditing = searchParams.get('intent') === 'edit';
  const [draftTemplateToMakeEffective, setDraftTemplateToMakeEffective] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showChangeOwnerModal, setShowChangeOwnerModal] = useState<boolean>(false);
  const { mutate: deleteTemplateDraft, isLoading: isDeleteTemplateDraftLoading } = useDeleteTemplateDraft();

  const handleEditClick = useCallback(() => setSearchParams({ intent: 'edit' }), [setSearchParams]);

  const handleDeleteTemplateDraftClick = useCallback(() => {
    deleteTemplateDraft(template.template_uuid);
  }, [deleteTemplateDraft, template]);

  const kebabMenuItems = useMemo(() => {
    const items: { id: string; label: string }[] = [];
    if (canChangeOwner) {
      items.push({ id: 'changeOwner', label: t('template.details.buttons.changeOwner') });
    }
    if (canDelete) {
      items.push({ id: 'delete', label: t('template.details.buttons.delete') });
    }
    return items;
  }, [canChangeOwner, canDelete, t]);

  const handleMenuClick = useCallback((item: QMenuItemType) => {
    if (item.id === 'delete') {
      setShowDeleteModal(true);
    }
    if (item.id === 'changeOwner') {
      setShowChangeOwnerModal(true);
    }
  }, []);

  const disableMakeEffective = template.sections === null || template.sections.length === 0;
  const showEditButton = canEdit && !isEditing;
  const showEditingActions = canEdit && isEditing;

  if (!canEdit && !canMakeEffective && kebabMenuItems.length === 0) {
    return null;
  }

  return (
    <>
      <QButtonGroup>
        {showEditButton && (
          <QButton onClick={handleEditClick} variant="outline" data-cy="editButton" leftIcon="Edit3">
            {t('template.details.buttons.edit')}
          </QButton>
        )}
        {showEditingActions && (
          <>
            <QButton variant="ghost" onClick={() => setSearchParams({})} data-cy="cancelCCSettingsButton">
              {t('common.buttons.cancel')}
            </QButton>
            <QButton leftIcon="Save" onClick={onSaveSections} data-cy="saveCCSettingsButton">
              {t('common.buttons.save')}
            </QButton>
          </>
        )}
        {canMakeEffective && (
          <MakeEffectiveButton
            isDisabled={disableMakeEffective}
            isEditing={isEditing}
            onClick={() => setDraftTemplateToMakeEffective(template.template_uuid)}
          />
        )}
        {kebabMenuItems.length > 0 && (
          <QBox data-cy="dropdownMenuMoreOption">
            <QMenuButton
              buttonLabel="options"
              itemSize="sm"
              items={kebabMenuItems}
              onItemClick={handleMenuClick}
              variant="icon"
            />
          </QBox>
        )}
      </QButtonGroup>
      {draftTemplateToMakeEffective && (
        <MakeEffectiveModal
          templateUuid={draftTemplateToMakeEffective}
          onClose={() => setDraftTemplateToMakeEffective(null)}
          onBeforeSubmit={onBeforeMakeEffective}
        />
      )}
      {showDeleteModal && (
        <QDeleteConfirmationModal
          title={t('template.details.deleteModal.title')}
          onConfirm={handleDeleteTemplateDraftClick}
          inProgress={isDeleteTemplateDraftLoading}
          onClose={() => setShowDeleteModal(false)}
          message={<QText>{t('template.details.deleteModal.message')}</QText>}
        />
      )}
      {showChangeOwnerModal && (
        <ChangeTemplateOwner template={template} onClose={() => setShowChangeOwnerModal(false)} />
      )}
    </>
  );
};

type MakeEffectiveButtonProps = {
  isDisabled: boolean;
  isEditing: boolean;
  onClick: () => void;
};

const MakeEffectiveButton: React.FC<MakeEffectiveButtonProps> = ({ isDisabled, isEditing, onClick }) => {
  const { t } = useTranslation();

  if (isDisabled) {
    return (
      <QTooltip label={t('template.makeEffective.tooltip.noSections')} placement="top">
        <QBox>
          <QButton data-cy="makeEffectiveButton" isDisabled>
            {t('template.details.buttons.makeEffective')}
          </QButton>
        </QBox>
      </QTooltip>
    );
  }

  return (
    <QButton onClick={onClick} data-cy="makeEffectiveButton" isDisabled={isEditing}>
      {t('template.details.buttons.makeEffective')}
    </QButton>
  );
};
